import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  AlertTriangle,
  AppWindow,
  ArrowLeftRight,
  BadgeCheck,
  BadgeDollarSign,
  BookOpen,
  BookUser,
  Boxes,
  Building2,
  CalendarDays,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  Contact,
  DraftingCompass,
  Factory,
  Briefcase,
  FileSignature,
  FileText,
  Files,
  FolderKanban,
  Forklift,
  Inbox,
  Gauge,
  GitCompareArrows,
  Globe,
  GraduationCap,
  Grid3x3,
  Hammer,
  HandCoins,
  Handshake,
  IdCard,
  KeyRound,
  Landmark,
  Layers,
  LayoutDashboard,
  LineChart,
  ListChecks,
  ListTodo,
  Megaphone,
  MessageSquareQuote,
  MapPin,
  MessageCircleWarning,
  MonitorSmartphone,
  Network,
  Package,
  Palette,
  Palmtree,
  PiggyBank,
  Plug,
  Printer,
  ReceiptText,
  RefreshCcw,
  Scale,
  ScanBarcode,
  ScrollText,
  Settings2,
  Share2,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Tag,
  Trash2,
  Target,
  Truck,
  Sparkles,
  TrendingUp,
  Undo2,
  Users,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/app/AuthProvider';
import { canApprovePto, canRead, isSuperAdmin, vis, type Role, type SectionAccess } from '@/lib/permissions';
import { EQUIPMENT_CATEGORIES, EQUIPMENT_CATEGORY_META } from '@/features/equipment/model';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { useProjects } from '@/features/projects/hooks';
import { useEngProjects } from '@/features/engineering/hooks';
import { useHasOnlineAccounts } from '@/features/onlineAccounts/hooks';

interface PaletteCommand {
  group: string;
  label: string;
  icon: LucideIcon;
  to: string;
  visible: (
    role: Role | null,
    sectionAccess?: SectionAccess | null,
    ctx?: { hasOnlineAccounts: boolean },
  ) => boolean;
}

const COMMANDS: PaletteCommand[] = [
  { group: 'Go', label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard', visible: vis('dashboard') },
  { group: 'Go', label: 'Calendar', icon: CalendarDays, to: '/calendar', visible: vis('calendar') },
  { group: 'Go', label: 'Digital Company', icon: Building2, to: '/digital-company', visible: vis('digital_office') },
  { group: 'Go', label: 'Ticket inbox', icon: Inbox, to: '/tickets', visible: vis('tickets') },
  { group: 'Go', label: 'My tasks', icon: ListTodo, to: '/tasks', visible: vis('projects') },

  // Engineering — projects, CAD file drive and the 3D print log.
  { group: 'Engineering', label: 'Engineering projects', icon: DraftingCompass, to: '/engineering', visible: vis('engineering') },
  { group: 'Engineering', label: '3D print log', icon: Printer, to: '/engineering/prints', visible: vis('engineering') },

  // HR — people-ops. Manage PTO mirrors sidebar / App.tsx approver gate.
  { group: 'HR', label: 'My PTO', icon: Palmtree, to: '/hr/pto', visible: vis('pto') },
  { group: 'HR', label: 'Manage PTO', icon: ClipboardCheck, to: '/hr/pto/manage', visible: (r) => canApprovePto(r) },
  { group: 'HR', label: 'Employees', icon: Users, to: '/hr/employees', visible: (r) => canApprovePto(r) },
  { group: 'HR', label: 'Org Chart', icon: Network, to: '/hr/org-chart', visible: vis('hr') },
  { group: 'HR', label: 'Employee Reviews', icon: Star, to: '/hr/reviews', visible: vis('hr') },
  { group: 'HR', label: 'Handbook & Policies', icon: BookOpen, to: '/hr/handbook', visible: vis('hr') },
  { group: 'HR', label: 'Personal Documents', icon: IdCard, to: '/hr/documents', visible: vis('hr') },
  { group: 'HR', label: 'Complaints', icon: MessageCircleWarning, to: '/hr/complaints', visible: vis('hr') },

  { group: 'Financials', label: 'Main Financials', icon: TrendingUp, to: '/financials/projections', visible: vis('financials') },
  { group: 'Financials', label: 'Main Financials · P&L', icon: TrendingUp, to: '/financials/projections?tab=pnl', visible: vis('financials') },
  { group: 'Financials', label: 'Main Financials · Balance sheet', icon: Scale, to: '/financials/projections?tab=balance-sheet', visible: vis('financials') },
  { group: 'Financials', label: 'Main Financials · Cash flow', icon: PiggyBank, to: '/financials/projections?tab=cashflow', visible: vis('financials') },
  { group: 'Financials', label: 'Main Financials · Sales drivers', icon: TrendingUp, to: '/financials/projections?tab=sales', visible: vis('financials') },
  { group: 'Financials', label: 'Main Financials · AR & AP', icon: ReceiptText, to: '/financials/projections?tab=ar-ap', visible: vis('financials') },
  { group: 'Financials', label: 'Main Financials · Insights', icon: Sparkles, to: '/financials/projections?tab=insights', visible: vis('financials') },
  { group: 'Financials', label: 'Accounts', icon: Wallet, to: '/financials/bank-accounts', visible: vis('financials') },
  { group: 'Financials', label: 'Transactions', icon: ArrowLeftRight, to: '/financials/transactions', visible: vis('financials') },
  { group: 'Financials', label: 'Amazon Business', icon: ShoppingCart, to: '/financials/amazon-business', visible: vis('financials') },
  { group: 'Financials', label: 'Reconciliation', icon: GitCompareArrows, to: '/financials/reconciliation', visible: vis('financials') },
  { group: 'Financials', label: 'Debt balances', icon: Scale, to: '/financials/debt-balances', visible: vis('financials') },
  // Loans uses the dedicated `loans` module flag so external_lender
  // sees the parent + Family entries in the palette without unlocking
  // the rest of financials. SBA stays super_admin-only — matches the
  // sidebar + route gates.
  { group: 'Financials', label: 'Loans', icon: HandCoins, to: '/financials/loans', visible: vis('loans') },
  { group: 'Financials', label: 'Loans · Family', icon: Users, to: '/financials/loans/family', visible: vis('loans') },
  { group: 'Financials', label: 'Loans · SBA', icon: Landmark, to: '/financials/loans/sba', visible: isSuperAdmin },

  { group: 'Catalog', label: 'Items', icon: Package, to: '/catalog/items', visible: vis('catalog') },
  { group: 'Catalog', label: 'Equipment', icon: Forklift, to: '/catalog/equipment', visible: vis('catalog') },
  ...EQUIPMENT_CATEGORIES.map((category) => ({
    group: 'Catalog',
    label: EQUIPMENT_CATEGORY_META[category].label,
    icon: EQUIPMENT_CATEGORY_META[category].icon,
    to: `/catalog/equipment/category/${category}`,
    visible: vis('catalog'),
  })),
  { group: 'Catalog', label: 'Customers', icon: Contact, to: '/catalog/customers', visible: (r, a) => vis('catalog')(r, a) && vis('sales')(r, a) },
  { group: 'Catalog', label: 'Suppliers', icon: Factory, to: '/catalog/suppliers', visible: (r, a) => vis('catalog')(r, a) && vis('procurement')(r, a) },
  { group: 'Catalog', label: 'Software · Subscriptions', icon: AppWindow, to: '/catalog/software/subscriptions', visible: vis('catalog') },
  { group: 'Catalog', label: 'Software · Accounts & passwords', icon: KeyRound, to: '/catalog/software/accounts', visible: (_r, _a, ctx) => !!ctx?.hasOnlineAccounts },

  { group: 'Inventory', label: 'On-hand', icon: Boxes, to: '/inventory/levels', visible: vis('inventory') },
  { group: 'Inventory', label: 'Locations', icon: MapPin, to: '/inventory/locations', visible: vis('inventory') },
  { group: 'Inventory', label: 'Stock movements', icon: RefreshCcw, to: '/inventory/movements', visible: vis('inventory') },
  { group: 'Inventory', label: 'Adjustments', icon: SlidersHorizontal, to: '/inventory/adjustments', visible: vis('inventory') },
  { group: 'Inventory', label: 'Cycle count', icon: ListChecks, to: '/inventory/cycle-count', visible: vis('inventory') },

  { group: 'Procurement', label: 'Procurement calendar (what to order, when, one-click draft POs)', icon: CalendarDays, to: '/procurement/calendar', visible: vis('procurement') },
  { group: 'Procurement', label: 'Purchase orders', icon: ClipboardList, to: '/procurement/purchase-orders', visible: vis('procurement') },
  { group: 'Procurement', label: 'Receiving', icon: Truck, to: '/procurement/receiving', visible: vis('procurement') },
  { group: 'Procurement', label: 'Vendor invoices', icon: ReceiptText, to: '/procurement/vendor-invoices', visible: vis('procurement') },

  { group: 'Manufacturing', label: 'Production', icon: Factory, to: '/mfg/production', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Employee matrix (workstation sign-offs)', icon: Grid3x3, to: '/mfg/production/employee-matrix', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Unit log (serial numbers, photos, lots, shipments)', icon: ScanBarcode, to: '/mfg/production/units', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Scrap log', icon: Trash2, to: '/mfg/scrap-log', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Workstations (live benches, defects, feedback)', icon: Hammer, to: '/mfg/workstations', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Work Mode (station tablet)', icon: MonitorSmartphone, to: '/work', visible: vis('work_mode') },
  { group: 'Manufacturing', label: 'BOMs', icon: Layers, to: '/mfg/boms', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Work schedules', icon: Settings2, to: '/mfg/work-schedules', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Work orders', icon: ClipboardList, to: '/mfg/work-orders', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Production planning (targets, procurement calendar, draft POs)', icon: CalendarRange, to: '/mfg/planning', visible: vis('manufacturing') },
  { group: 'Manufacturing', label: 'Quality', icon: ShieldCheck, to: '/mfg/quality', visible: vis('manufacturing') },

  { group: 'Sales', label: 'Orders', icon: ShoppingCart, to: '/sales/orders', visible: vis('sales') },
  { group: 'Sales', label: 'Quotes', icon: Tag, to: '/sales/quotes', visible: vis('sales') },
  { group: 'Sales', label: 'Shipments', icon: Truck, to: '/sales/shipments', visible: vis('sales') },
  { group: 'Sales', label: 'Returns / RMA', icon: Undo2, to: '/sales/returns', visible: vis('sales') },
  { group: 'Sales', label: 'Customer Feedback', icon: MessageSquareQuote, to: '/sales/feedback', visible: vis('sales') },

  { group: 'Quality / ISO', label: 'Documents', icon: FileText, to: '/iso/documents', visible: vis('sops') },
  { group: 'Quality / ISO', label: 'Training', icon: GraduationCap, to: '/iso/training', visible: vis('sops') },
  { group: 'Quality / ISO', label: 'Calibration', icon: Wrench, to: '/iso/calibration', visible: vis('sops') },
  { group: 'Quality / ISO', label: 'Supplier qualification', icon: Building2, to: '/iso/suppliers', visible: vis('sops') },
  { group: 'Quality / ISO', label: 'NCR / CAPA', icon: AlertTriangle, to: '/iso/ncr-capa', visible: vis('sops') },
  { group: 'Quality / ISO', label: 'Audits', icon: ClipboardCheck, to: '/iso/audits', visible: vis('sops') },

  { group: 'Marketing', label: 'Sales & Marketing', icon: TrendingUp, to: '/marketing/sales-marketing', visible: vis('marketing') },
  { group: 'Marketing', label: 'Marketing dashboard', icon: Megaphone, to: '/marketing/dashboard', visible: vis('marketing') },
  { group: 'Marketing', label: 'Web analytics (virrolife.com)', icon: Globe, to: '/marketing/web-analytics', visible: vis('marketing') },
  { group: 'Marketing', label: 'Social media', icon: Share2, to: '/marketing/social-media', visible: vis('marketing') },
  { group: 'Marketing', label: 'Competitors', icon: Target, to: '/marketing/competitors', visible: vis('marketing') },
  { group: 'Marketing', label: 'Affiliates', icon: Handshake, to: '/marketing/affiliates', visible: vis('marketing') },
  { group: 'Marketing', label: 'Advertising', icon: BadgeDollarSign, to: '/marketing/advertising', visible: vis('marketing') },

  { group: 'Intelligence', label: 'Forecast', icon: LineChart, to: '/intel/forecast', visible: vis('intelligence') },
  { group: 'Intelligence', label: 'KPIs', icon: Gauge, to: '/intel/kpis', visible: vis('intelligence') },

  { group: 'Admin', label: 'Templates', icon: Files, to: '/admin/templates', visible: vis('company_admin') },
  { group: 'Admin', label: 'Company profile', icon: Briefcase, to: '/admin/company', visible: vis('company_admin') },
  { group: 'Admin', label: 'Licenses & insurance', icon: BadgeCheck, to: '/admin/licenses', visible: vis('company_admin') },
  { group: 'Admin', label: 'Contracts', icon: FileSignature, to: '/admin/contracts', visible: vis('company_admin') },
  { group: 'Admin', label: 'Key contacts', icon: BookUser, to: '/admin/contacts', visible: vis('company_admin') },
  { group: 'Admin', label: 'Brand kit', icon: Palette, to: '/admin/brand', visible: vis('company_admin') },

  { group: 'Settings', label: 'Integrations', icon: Plug, to: '/integrations', visible: vis('integrations') },
  { group: 'Settings', label: 'Roles & users', icon: Users, to: '/users', visible: isSuperAdmin },
  { group: 'Settings', label: 'Audit log', icon: ScrollText, to: '/audit', visible: vis('audit') },
];

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { role, profile } = useAuth();
  const navigate = useNavigate();
  // Pull live projects so they show up as searchable jump targets too.
  // RLS already restricts what we get back, so any role with read
  // access to the projects module sees only their projects in the
  // palette.
  const { data: projects } = useProjects();
  const canSeeEngineering = canRead(role, 'engineering', profile?.section_access);
  const { data: engProjects } = useEngProjects({ enabled: canSeeEngineering });

  const hasOnlineAccounts = useHasOnlineAccounts();
  const visible = COMMANDS.filter((c) =>
    c.visible(role, profile?.section_access, { hasOnlineAccounts }),
  );
  const groups = Array.from(new Set(visible.map((c) => c.group)));
  const projectCommands = canRead(role, 'projects', profile?.section_access)
    ? (projects ?? []).map((p) => ({
        id: p.id,
        label: p.name,
        to: `/projects/${p.id}`,
      }))
    : [];
  const engProjectCommands = canSeeEngineering
    ? (engProjects ?? []).map((p) => ({
        id: p.id,
        label: `${p.code} · ${p.name}`,
        to: `/engineering/${p.id}`,
      }))
    : [];

  const handleSelect = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0" dismissOnOutsideClick>
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">
          Jump to any page in Atlas.
        </DialogDescription>
        <Command className="flex w-full flex-col" loop>
          <Command.Input
            placeholder="Type to search…"
            className="h-12 w-full border-b border-border bg-transparent px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <Command.List className="max-h-[60vh] overflow-y-auto p-2">
            <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
              No results.
            </Command.Empty>
            {groups.map((group) => (
              <Command.Group
                key={group}
                heading={group}
                className="mb-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-info"
              >
                {visible
                  .filter((c) => c.group === group)
                  .map((c) => (
                    <Command.Item
                      key={c.to}
                      value={`${c.group} ${c.label}`}
                      onSelect={() => handleSelect(c.to)}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground data-[selected=true]:bg-info-soft/25 data-[selected=true]:text-info [&[data-selected=true]_svg]:text-info"
                    >
                      <c.icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
                      {c.label}
                    </Command.Item>
                  ))}
              </Command.Group>
            ))}
            {projectCommands.length > 0 && (
              <Command.Group
                heading="Projects"
                className="mb-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-info"
              >
                {projectCommands.map((p) => (
                  <Command.Item
                    key={p.id}
                    value={`Projects ${p.label}`}
                    onSelect={() => handleSelect(p.to)}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground data-[selected=true]:bg-info-soft/25 data-[selected=true]:text-info [&[data-selected=true]_svg]:text-info"
                  >
                    <FolderKanban className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
                    {p.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
            {engProjectCommands.length > 0 && (
              <Command.Group
                heading="Engineering projects"
                className="mb-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-info"
              >
                {engProjectCommands.map((p) => (
                  <Command.Item
                    key={p.id}
                    value={`Engineering ${p.label}`}
                    onSelect={() => handleSelect(p.to)}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground data-[selected=true]:bg-info-soft/25 data-[selected=true]:text-info [&[data-selected=true]_svg]:text-info"
                  >
                    <DraftingCompass className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
                    {p.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
